import DiffMatchPatch from "diff-match-patch";

const dmp = new DiffMatchPatch();

/* Strict line-by-line diff. Each changed line shows old (strikethrough) directly
   followed by new (green). No jumbling — every change appears next to its original. */
export function computeLineDiff(originalText, optimizedText) {
  const origLines = (originalText || "").split("\n").filter((l) => l.trim());
  const optLines = (optimizedText || "").split("\n").filter((l) => l.trim());
  const result = [];
  let oi = 0, ni = 0;

  while (oi < origLines.length && ni < optLines.length) {
    const oLine = origLines[oi];
    const nLine = optLines[ni];
    if (oLine === nLine) { result.push({ type: "unchanged", text: oLine }); oi++; ni++; continue; }

    const sim = computeLineSimilarity(oLine, nLine);
    if (sim > 0.25) {
      result.push({ type: "modified", origLine: oLine, newLine: nLine, wordDiffs: computeWordDiffForLine(oLine, nLine) });
      oi++; ni++; continue;
    }

    let bestOrigMatch = -1, bestOrigSim = 0, bestOptMatch = -1, bestOptSim = 0;
    for (let k = ni + 1; k < Math.min(ni + 5, optLines.length); k++) {
      const s = computeLineSimilarity(oLine, optLines[k]);
      if (s > bestOptSim && s > 0.25) { bestOptSim = s; bestOptMatch = k; }
    }
    for (let k = oi + 1; k < Math.min(oi + 5, origLines.length); k++) {
      const s = computeLineSimilarity(origLines[k], nLine);
      if (s > bestOrigSim && s > 0.25) { bestOrigSim = s; bestOrigMatch = k; }
    }

    if (bestOptMatch > ni && bestOptSim > bestOrigSim) {
      for (let k = ni; k < bestOptMatch; k++) result.push({ type: "added", text: optLines[k] });
      ni = bestOptMatch;
    } else if (bestOrigMatch > oi && bestOrigSim > bestOptSim) {
      for (let k = oi; k < bestOrigMatch; k++) result.push({ type: "removed", text: origLines[k] });
      oi = bestOrigMatch;
    } else {
      result.push({ type: "modified", origLine: oLine, newLine: nLine, wordDiffs: computeWordDiffForLine(oLine, nLine) });
      oi++; ni++;
    }
  }
  while (oi < origLines.length) { result.push({ type: "removed", text: origLines[oi] }); oi++; }
  while (ni < optLines.length) { result.push({ type: "added", text: optLines[ni] }); ni++; }
  return result;
}

export function computeLineSimilarity(a, b) {
  if (!a && !b) return 1;
  if (!a || !b) return 0;
  const wordsA = a.toLowerCase().split(/\s+/).filter(Boolean);
  const wordsB = b.toLowerCase().split(/\s+/).filter(Boolean);
  if (wordsA.length === 0 && wordsB.length === 0) return 1;
  const setA = new Set(wordsA);
  let common = 0;
  for (const w of wordsB) if (setA.has(w)) common++;
  return common / Math.max(wordsA.length, wordsB.length);
}

export function computeWordDiffForLine(origLine, newLine) {
  const d = dmp.diff_main(origLine, newLine);
  dmp.diff_cleanupSemantic(d);
  const wordDiffs = [];
  for (const [op, text] of d) {
    const parts = text.match(/\S+|\s+/g) || [text];
    for (const part of parts) wordDiffs.push([op, part]);
  }
  return wordDiffs;
}

export function cleanLine(text) {
  if (!text) return text;
  if (text.trim().startsWith("[TABLE_CELL]")) return text.trim().replace("[TABLE_CELL]", "").trim();
  return text;
}
